import React, { useEffect, useState, useMemo } from "react";
import { Loader2, Calculator, Save, Trash2, TrendingUp, TrendingDown, Download } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../../lib/api";
import { downloadFile } from "../../../lib/download";
import { formatApiErrorDetail } from "../../../lib/constants";

const gbp = (n) => "£" + Number(n || 0).toFixed(2);
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/London" }) : "—";

const Stat = ({ label, value, testId, strong }) => (
  <div className={`rounded-lg border p-4 ${strong ? "bg-[#F2F8F2] border-green-200" : "bg-white border-gray-200"}`}>
    <p className="text-xs text-gray-500">{label}</p>
    <p data-testid={testId} className="text-xl font-bold text-[#0A1F13] mt-1">{value}</p>
  </div>
);

const RevenueBreakdown = ({ data, productRevenue }) => (
  <div data-testid="revenue-breakdown" className="space-y-2">
    <h4 className="text-sm font-semibold text-[#0A1F13]">Revenue breakdown</h4>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Stat label="Product sales" value={gbp(productRevenue)} testId="product-sales-value" />
      <Stat label="Fixed charge" value={gbp(data.service_total)} testId="fixed-charge-value" />
      <Stat label="Delivery fees" value={gbp(data.delivery_total)} testId="delivery-fees-value" />
      <Stat label="Fixed charge + delivery" value={gbp((data.service_total || 0) + (data.delivery_total || 0))} testId="charges-total-value" strong />
    </div>
  </div>
);

const CostInput = ({ label, hint, value, onChange, testId }) => (
  <label className="block">
    <span className="text-sm font-medium text-[#0A1F13]">{label}</span>
    <span className="block text-xs text-gray-400 mb-1">{hint}</span>
    <div className="flex items-center gap-1">
      <span className="text-gray-400">£</span>
      <input data-testid={testId} type="number" min="0" step="0.01" inputMode="decimal" value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 rounded-md bg-gray-100 focus:bg-white border border-gray-200 focus:border-[#228B22] outline-none" />
    </div>
  </label>
);

const ExtraCosts = ({ extra, setExtra }) => (
  <div data-testid="extra-costs" className="bg-white rounded-lg border border-gray-200 p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
    <CostInput label="Delivery costs" hint="Fuel, driver, etc." value={extra.delivery_cost} testId="delivery-cost-input"
      onChange={(v) => setExtra((e) => ({ ...e, delivery_cost: v }))} />
    <CostInput label="Other costs" hint="Packaging, bags, market fees, etc." value={extra.other_cost} testId="other-cost-input"
      onChange={(v) => setExtra((e) => ({ ...e, other_cost: v }))} />
  </div>
);

const ProfitSplit = ({ result }) => (
  <div data-testid="profit-split" className="bg-white rounded-lg border border-gray-200 p-5 text-sm space-y-2">
    <h4 className="font-semibold text-[#0A1F13]">Where the profit comes from</h4>
    <Row label="Product margin (sales − product cost)" value={result.product_margin} testId="product-margin-value" />
    <Row label="Fixed charge + delivery fees" value={result.charges_total} testId="split-charges-value" />
    <Row label="Less delivery & other costs" value={-((result.delivery_cost || 0) + (result.other_cost || 0))} testId="split-extra-costs-value" />
    <div className="border-t border-gray-100 pt-2">
      <Row label="Profit / loss" value={result.profit_loss} testId="split-profit-value" bold />
    </div>
  </div>
);

const ExportButtons = ({ saved, dayId, date }) => {
  const [busy, setBusy] = useState(null);
  const run = async (format) => {
    setBusy(format);
    try {
      const day = date ? new Date(date).toLocaleDateString("en-CA", { timeZone: "Europe/London" }) : "review";
      await downloadFile(`/admin/market-review/export?format=${format}&market_day_id=${dayId}`, `market-review-${day}.${format}`);
    } catch (err) {
      toast.error("Export failed — save the review first");
    } finally {
      setBusy(null);
    }
  };
  return ["pdf", "csv"].map((f) => (
    <button key={f} data-testid={`export-review-${f}-button`} onClick={() => run(f)} disabled={!saved || !!busy}
      title={saved ? "" : "Save the review to enable export"}
      className="h-11 px-5 rounded-md border border-gray-300 text-[#0A1F13] hover:bg-gray-50 font-semibold flex items-center gap-2 transition-colors disabled:opacity-40">
      {busy === f ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />} {f === "pdf" ? "PDF" : "Spreadsheet"}
    </button>
  ));
};

const MonthlyExport = () => {
  const [busy, setBusy] = useState(null);
  const run = async (format) => {
    setBusy(format);
    try {
      await downloadFile(`/admin/market-review/monthly-export?format=${format}`, `monthly-profit-summary.${format}`);
    } catch (err) {
      toast.error("Monthly summary download failed");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500">Monthly summary:</span>
      {["pdf", "csv"].map((f) => (
        <button key={f} data-testid={`monthly-export-${f}-button`} onClick={() => run(f)} disabled={!!busy}
          className="h-9 px-3 rounded-md border border-gray-300 text-sm text-[#0A1F13] hover:bg-gray-50 font-medium flex items-center gap-1.5 transition-colors disabled:opacity-40">
          {busy === f ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {f === "pdf" ? "PDF" : "Spreadsheet"}
        </button>
      ))}
    </div>
  );
};

const ReviewHistory = ({ history, activeId, onOpen }) => (
  <div data-testid="review-history" className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
    <div className="px-5 pt-5 pb-3 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h4 className="font-semibold text-[#0A1F13]">Review history</h4>
        <p className="text-xs text-gray-500">Saved reviews only. Tap a market day to open and edit it.</p>
      </div>
      {history.length > 0 && <MonthlyExport />}
    </div>
    {history.length === 0 ? (
      <p data-testid="review-history-empty" className="px-5 pb-5 text-sm text-gray-400">No saved reviews yet.</p>
    ) : (
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left text-gray-500">
            <th className="px-4 py-2 font-medium">Delivery date</th>
            <th className="px-4 py-2 font-medium text-right">Revenue</th>
            <th className="px-4 py-2 font-medium text-right">Fixed + delivery</th>
            <th className="px-4 py-2 font-medium text-right">Total cost</th>
            <th className="px-4 py-2 font-medium text-right">Profit / loss</th>
          </tr>
        </thead>
        <tbody>
          {history.map((h) => (
            <tr key={h.market_day_id} data-testid={`history-row-${h.market_day_id}`} onClick={() => onOpen(h.market_day_id)}
              className={`border-t border-gray-100 cursor-pointer hover:bg-green-50 transition-colors ${h.market_day_id === activeId ? "bg-green-50" : ""}`}>
              <td className="px-4 py-3 text-[#228B22] font-medium">{fmtDate(h.delivery_date)}</td>
              <td className="px-4 py-3 text-right">{gbp(h.total_revenue)}</td>
              <td className="px-4 py-3 text-right text-gray-600">{h.charges_total == null ? "—" : gbp(h.charges_total)}</td>
              <td className="px-4 py-3 text-right">{gbp(h.total_cost)}</td>
              <td className={`px-4 py-3 text-right font-semibold ${h.profit_loss >= 0 ? "text-green-700" : "text-red-600"}`}>{h.profit_loss < 0 ? "−" : ""}{gbp(Math.abs(h.profit_loss))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const Row = ({ label, value, testId, bold }) => (
  <div className={`flex justify-between ${bold ? "font-bold" : ""}`}>
    <span className="text-gray-600">{label}</span>
    <span data-testid={testId} className={value < 0 ? "text-red-600" : "text-[#0A1F13]"}>{value < 0 ? "−" : ""}{gbp(Math.abs(value))}</span>
  </div>
);

export default function AdminMarketReview() {
  const [dayId, setDayId] = useState(null);
  const [history, setHistory] = useState([]);
  const params = dayId ? { market_day_id: dayId } : {};
  const [data, setData] = useState(null);
  const [actual, setActual] = useState({});
  const [result, setResult] = useState(null);
  const [extra, setExtra] = useState({ delivery_cost: "", other_cost: "" });
  const [saving, setSaving] = useState(false);

  const load = async (keepResult = false) => {
    setData(null);
    if (!keepResult) setResult(null);
    try {
      const { data } = await adminApi.get("/admin/market-review", { params });
      setData(data);
      adminApi.get("/admin/market-review/history").then((r) => setHistory(r.data)).catch(() => {});
      if (data.saved?.actual_prices) {
        setActual(data.saved.actual_prices);
        setExtra({ delivery_cost: data.saved.delivery_cost || "", other_cost: data.saved.other_cost || "" });
      } else {
        setActual({});
        setExtra({ delivery_cost: "", other_cost: "" });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
      setData({ market_day: null });
    }
  };

  useEffect(() => { load(); }, [dayId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPrice = (pid, val) => setActual((a) => ({ ...a, [pid]: val }));

  const calc = useMemo(() => {
    if (!data?.items) return null;
    let revenue = 0, cost = 0;
    const lines = data.items.map((it) => {
      const ap = parseFloat(actual[it.product_id]) || 0;
      const lineCost = ap * it.quantity;
      revenue += it.market_revenue;
      cost += lineCost;
      return { ...it, actual_price: ap, line_cost: lineCost, line_profit: it.market_revenue - lineCost };
    });
    const charges = (data.service_total || 0) + (data.delivery_total || 0);
    const dc = parseFloat(extra.delivery_cost) || 0;
    const oc = parseFloat(extra.other_cost) || 0;
    const totalCost = cost + dc + oc;
    return {
      lines,
      product_revenue: revenue,
      product_cost: cost,
      product_margin: revenue - cost,
      charges_total: charges,
      delivery_cost: dc,
      other_cost: oc,
      total_revenue: revenue + charges,
      total_cost: totalCost,
      profit_loss: revenue + charges - totalCost,
    };
  }, [data, actual, extra]);

  useEffect(() => {
    if (data?.saved && calc && !result) setResult(calc);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const doCalculate = () => {
    if (!calc) return;
    setResult(calc);
    toast.success("Profit / loss calculated");
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const { data: res } = await adminApi.post("/admin/market-review", {
        actual_prices: actual,
        delivery_cost: parseFloat(extra.delivery_cost) || 0,
        other_cost: parseFloat(extra.other_cost) || 0,
      }, { params });
      setResult({ ...res, lines: res.items });
      toast.success("Market review saved");
      await load(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      await adminApi.delete("/admin/market-review", { params });
      setActual({});
      setResult(null);
      toast.success("Review deleted — you can redo it");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#228B22]" size={28} /></div>;

  const historyEl = <ReviewHistory history={history} activeId={data.market_day?.id} onOpen={setDayId} />;

  if (!data.market_day) {
    return (
      <div className="space-y-5">
      <div data-testid="review-empty" className="bg-white rounded-lg border border-gray-200 p-10 text-center text-gray-500">
        No closed market day yet. Once a market day ends, its review will appear here.
      </div>
      {historyEl}
      </div>
    );
  }

  if (!data.has_orders) {
    return (
      <div className="space-y-5">
      <div data-testid="review-no-orders" className="bg-white rounded-lg border border-gray-200 p-10 text-center text-gray-500">
        No orders were placed during the last market day (delivery {fmtDate(data.market_day.delivery_date)}), so there is nothing to review.
      </div>
      {historyEl}
      </div>
    );
  }

  const profit = result?.profit_loss ?? 0;
  const isProfit = profit >= 0;

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-bold text-[#0A1F13]">Market Review</h3>
            <p className="text-sm text-gray-500">{dayId ? "Past" : "Last"} market day — delivery {fmtDate(data.market_day.delivery_date)}. Enter the actual price you paid per unit, then calculate.</p>
            {dayId && (
              <button data-testid="back-to-latest-button" onClick={() => setDayId(null)} className="text-sm text-[#228B22] font-medium mt-1 hover:underline">← Back to latest market day</button>
            )}
          </div>
          {data.saved && (
            <span data-testid="review-saved-badge" className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-50 text-green-700">Saved review loaded</span>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table data-testid="market-review-table" className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-gray-500">
              <th className="px-4 py-3 font-medium">Product</th>
              <th className="px-4 py-3 font-medium text-right">Qty Sold</th>
              <th className="px-4 py-3 font-medium text-right">Market Day Price</th>
              <th className="px-4 py-3 font-medium text-right">Revenue</th>
              <th className="px-4 py-3 font-medium text-right">Actual Price (cost / unit)</th>
              {result && <th className="px-4 py-3 font-medium text-right">Line Profit</th>}
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => {
              const line = result?.lines?.find((l) => l.product_id === it.product_id);
              return (
                <tr key={it.product_id} className="border-t border-gray-100">
                  <td className="px-4 py-3 text-[#0A1F13]">{it.product_name}{it.unit ? <span className="text-gray-400"> / {it.unit}</span> : null}</td>
                  <td className="px-4 py-3 text-right">{it.quantity}</td>
                  <td className="px-4 py-3 text-right">{gbp(it.market_price)}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{gbp(it.market_revenue)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <span className="text-gray-400">£</span>
                      <input
                        data-testid={`actual-price-input-${it.product_id}`}
                        type="number" min="0" step="0.01" inputMode="decimal"
                        value={actual[it.product_id] ?? ""}
                        onChange={(e) => setPrice(it.product_id, e.target.value)}
                        className="w-24 h-9 px-2 text-right rounded-md bg-gray-100 focus:bg-white border border-gray-200 focus:border-[#228B22] outline-none"
                      />
                    </div>
                  </td>
                  {result && (
                    <td className={`px-4 py-3 text-right font-medium ${line && line.line_profit >= 0 ? "text-green-700" : "text-red-600"}`}>
                      {line ? gbp(line.line_profit) : "—"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <RevenueBreakdown data={data} productRevenue={calc?.product_revenue || 0} />
      <ExtraCosts extra={extra} setExtra={setExtra} />

      <div className="flex flex-wrap gap-3">
        <button data-testid="calculate-profit-button" onClick={doCalculate} className="h-11 px-5 rounded-md bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold flex items-center gap-2 transition-colors">
          <Calculator size={18} /> Calculate
        </button>
        <button data-testid="save-review-button" onClick={doSave} disabled={saving} className="h-11 px-5 rounded-md bg-[#1A4331] hover:bg-[#0A1F13] text-white font-semibold flex items-center gap-2 transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save
        </button>
        <ExportButtons saved={!!data.saved} dayId={data.market_day.id} date={data.market_day.delivery_date} />
        <button data-testid="delete-review-button" onClick={doDelete} disabled={saving || !data.saved} className="h-11 px-5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 font-semibold flex items-center gap-2 transition-colors disabled:opacity-40">
          <Trash2 size={18} /> Delete
        </button>
      </div>

      {result && (
        <div data-testid="review-result" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-sm text-gray-500">Total Revenue</p>
            <p className="text-2xl font-bold text-[#0A1F13] mt-1">{gbp(result.total_revenue)}</p>
            <p className="text-xs text-gray-400 mt-1">products + fixed charge + delivery fees</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-sm text-gray-500">Total Cost</p>
            <p data-testid="total-cost-value" className="text-2xl font-bold text-[#0A1F13] mt-1">{gbp(result.total_cost)}</p>
            <p className="text-xs text-gray-400 mt-1">products {gbp(result.product_cost)} + delivery {gbp(result.delivery_cost)} + other {gbp(result.other_cost)}</p>
          </div>
          <div className={`rounded-lg border p-5 ${isProfit ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
            <p className="text-sm text-gray-600 flex items-center gap-1">{isProfit ? <TrendingUp size={15} /> : <TrendingDown size={15} />} {isProfit ? "Profit" : "Loss"}</p>
            <p data-testid="profit-loss-value" className={`text-2xl font-bold mt-1 ${isProfit ? "text-green-700" : "text-red-600"}`}>{gbp(Math.abs(profit))}</p>
            <p className="text-xs text-gray-500 mt-1">{isProfit ? "Net profit for the market day" : "Net loss for the market day"}</p>
          </div>
        </div>
      )}
      {result && <ProfitSplit result={result} />}
      {historyEl}
    </div>
  );
}
